import React, { useEffect, useMemo, useRef, useState, FC } from 'react';
import Heading from '../@shared/heading';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import ProductNavigationSection from './components/productNavigationSection';
import ProductCardSection from './components/productCardSection';
import FindProductSection from './components/findProductSection';
import useStickyWrapper from '../../hooks/useStickyWrapper';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import useExperienceEditorPlaceholder from '../../hooks/useExperienceEditorPlaceholder';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import { documentService, windowService } from '../../utils/window.utils';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import { TCHHomeProductDashLinesItem, TCHHomeProductDashProps } from './types/productDashboard.types';
import { CHHomeProductDashModel } from './model/productDashboard.model';
import useMediaQuery from '../../hooks/useMediaQuery';
import Breakpoints from '../../constants/breakpoints.constants';
import Picture from '../@shared/picture';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import CTA from '../@shared/CTA';
import ctaTypes from '../../enums/CTA.enums';
import { googleLineSeriesClick } from '../../utils/analytics';
import { homeProductSelectorSeriesClick } from '../../constants/analytics.constants';
import './styles.scss';

const ProductDashboardEquipment: FC<TCHHomeProductDashProps> = (props) => {
  const isPlaceholderShown = useExperienceEditorPlaceholder(props);
  const apiResponse = isEmptyResponse(props);
  const model = useMemo(() => new CHHomeProductDashModel(props), [props]);
  const [cardTitle, setCardTitle] = useState<string | undefined>('');
  const [cardItems, setCardItems] = useState<Array<TCHHomeProductDashLinesItem> | undefined>([]);
  const rootElement = documentService()?.querySelector?.('#root > main > .product-dashboard') as HTMLElement;
  const rootMenuElement = documentService()?.querySelector?.(
    '#root > main > .product-dashboard > .product-dashboard__container'
  ) as HTMLElement;
  const stickyWrapperEle = useStickyWrapper();
  const [seriesTitleUrl, setSeriesTitleUrl] = useState<string | undefined>('');
  const [cardType, setCardType] = useState<'series' | 'model'>('series');
  const productRef = useRef<HTMLElement>(null);
  const navMenuRef = useRef<HTMLInputElement>(null);
  const isDesktop = useMediaQuery(`(min-width: ${Breakpoints['dsk']}px)`);
  const [cardTitleForAnalytics, setCardTitleForAnalytics] = useState<string | undefined>(model?.Items[0]?.title);
  const productsItemUrl = model?.baseUrl;
  let scrollPosition = 0;

  const detectScrollDirection = (): void => {
    const bodyScrollPosition = documentService().body.getBoundingClientRect().top;
    if (rootElement) {
      const elementScrollPosition = rootElement.getBoundingClientRect();
      const elementMenuScrollPosition = rootMenuElement.getBoundingClientRect();
      const stickyWrapperHeight = stickyWrapperEle?.getBoundingClientRect().height || 0;

      if (bodyScrollPosition > scrollPosition) {
        // scrolling up
        rootElement?.classList.remove('product-dashboard--scrollup');
        if (elementScrollPosition.top > 0 || elementMenuScrollPosition.top > stickyWrapperHeight) {
          rootElement?.classList.remove('product-dashboard--scrolldown');
          rootElement?.classList.remove('product-dashboard--scrollup');
        } else {
          rootElement?.classList.add('product-dashboard--scrollup');
        }
      } else {
        // scrolling down
        if (elementScrollPosition.top < 0 || elementMenuScrollPosition.top < stickyWrapperHeight) {
          rootElement?.classList.add('product-dashboard--scrolldown');
          rootElement?.classList.remove('product-dashboard--scrollup');
        }
      }
    }

    scrollPosition = bodyScrollPosition;
  };

  const isSerieSkipped = (lineTitleUrl): boolean => {
    const linesWithSkippedSeries = props.fields?.itemData?.fields?.customParams?.linesWithSkippedSeries ?? [];

    return linesWithSkippedSeries.indexOf(lineTitleUrl) >= 0;
  };

  useEffect(() => {
    windowService().addEventListener('scroll', detectScrollDirection, false);
    return (): void => {
      windowService().removeEventListener('scroll', detectScrollDirection);
    };
  }, [stickyWrapperEle]); // eslint-disable-line

  useEffect(() => {
    if (model?.Items?.length > 0) {
      const seriesCardLength = model?.Items?.[0]?.items?.length;
      const lineTitleUrl = model?.Items?.[0]?.titleUrl;
      const intializeCardTitleUrl =
        seriesCardLength === 1 && !isSerieSkipped(lineTitleUrl)
          ? `${lineTitleUrl}/${model?.Items?.[0]?.items?.[0]?.titleUrl}`
          : lineTitleUrl;
      const intializeCardItemsTitle = seriesCardLength === 1 ? model?.Items?.[0]?.items?.[0]?.title : '';
      const intializeCardItems = seriesCardLength === 1 ? model?.Items?.[0]?.items : model?.Items;

      setCardTitle(intializeCardItemsTitle);
      setCardItems(intializeCardItems);
      setSeriesTitleUrl(intializeCardTitleUrl);
      setCardType(seriesCardLength === 1 ? 'model' : 'series');
    }
  }, [model]);

  const onMenuClick = (indexValue): void => {
    const seriesCardItems = model.Items.filter((_, index) => index === indexValue);
    const lineTitleUrl = seriesCardItems?.[0]?.titleUrl;
    setCardTitleForAnalytics(seriesCardItems?.[0]?.title)

    if (seriesCardItems?.[0]?.items?.length === 1) {
      const url = isSerieSkipped(lineTitleUrl) ? lineTitleUrl : `${lineTitleUrl}/${seriesCardItems?.[0]?.items?.[0]?.titleUrl}`;

      setCardTitle(seriesCardItems?.[0]?.items?.[0]?.title);
      setCardItems(seriesCardItems?.[0]?.items);
      setSeriesTitleUrl(url);
      setCardType('model');
    } else {
      setCardTitle('');
      setCardItems(seriesCardItems);
      setSeriesTitleUrl(lineTitleUrl);
      setCardType('series');
    }
    if (isDesktop) {
      productRef.current?.scrollIntoView(true);
    } else {
      navMenuRef.current?.scrollIntoView(true);
    }
    productRef.current?.classList.add('product-dashboard--scrolldown');
  };

  const onCardClick = (cardItemsProps, cardIndex): void => {
    const modelCardItems = cardItemsProps.filter((_, index) => index === cardIndex);
    setCardTitle(modelCardItems?.[0]?.title);
    setCardItems(modelCardItems);
    setSeriesTitleUrl(`${seriesTitleUrl}/${modelCardItems?.[0]?.titleUrl}`);
    setCardType('model');
    if (isDesktop) {
      productRef.current?.scrollIntoView(true);
    } else {
      navMenuRef.current?.scrollIntoView(true);
    }
    productRef.current?.classList.add('product-dashboard--scrolldown');
  };

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return <ExperienceEditorComponentPlaceholder image={model.thumbnailEE} componentName={model.componentName} />;
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  return (
    <section className={`product-dashboard ${model.isHeaderShown ? '' : 'product-dashboard--no-headertext'}`} ref={productRef}>
      <Picture
        productImageProps={{
          desktopRendition: model.desktopBgImage,
          mobileRendition: model.mobileBgImage,
          pictureClassName: 'product-dashboard__bgImage',
        }}
      />
      {model.isHeaderShown && (
        <div className="product-dashboard__header-wrapper">
          <div className="product-dashboard__header">
            <Heading
              title={model?.params?.title}
              subtitle={model?.params?.description}
              link={model?.viewLinkData}
              editable={true}
              extraClassNameLink={'product-dashboard__header--text'}
            />
          </div>
          {model.priorModelCTAField && (
            <CTA
              type={ctaTypes['secondaryBlack']}
              field={model.priorModelCTAField}
              onClick={() => {
                googleLineSeriesClick(
                  homeProductSelectorSeriesClick,
                  cardTitleForAnalytics,
                  model.priorModelCTAField?.value?.title,
                  model.priorModelCTAField?.value?.title,
                  model.priorModelCTAField?.value?.href
                )
              }}
              extraClassName="product-dashboard__prior-model product-dashboard__prior-model--dsk"
            />
          )}
        </div>
      )}
      <div className="product-dashboard__container" ref={navMenuRef}>
        <ProductNavigationSection menuData={model.productLineItems} findProduct={model?.findProductLinkData} onClick={onMenuClick} />
        {cardItems && cardItems?.length > 0 && (
          <ProductCardSection
            cardTitle={cardTitle}
            cardItems={cardItems?.[0]?.items}
            onClick={onCardClick}
            seriesTitleUrl={seriesTitleUrl}
            productsItemUrl={productsItemUrl}
            cardType={cardType}
            cardTitleForAnalytics={cardTitleForAnalytics}
          />
        )}
        {model.priorModelCTAField && (
          <CTA
            type={ctaTypes['secondaryBlack']}
            field={model.priorModelCTAField}
            extraClassName="product-dashboard__prior-model product-dashboard__prior-model--mbl"
            onClick={() => {
              googleLineSeriesClick(
                homeProductSelectorSeriesClick,
                cardTitleForAnalytics,
                model.priorModelCTAField?.value?.title,
                model.priorModelCTAField?.value?.title,
                model.priorModelCTAField?.value?.href
              )
            }}
          />
        )}
        {model?.findProductLinkData?.value?.text && model?.findProductLinkData?.value?.href && (
          <FindProductSection findProduct={model?.findProductLinkData} />
        )}
      </div>
    </section>
  );
};

export default withSitecoreContext()(ProductDashboardEquipment);
